import PropTypes from 'prop-types';
import React from 'react';

import CmsBlock from 'Component/CmsBlock';
import Field from 'Component/Field';
import FIELD_TYPE from 'Component/Field/Field.config';
import FieldPhoneNumber from 'Component/FieldPhoneNumber';
import Form from 'Component/Form';
import FormAgreement from 'Component/FormAgreement';
import GdprConsent from 'Component/GdprConsent';
import MyAccountCompanyForm from 'Component/MyAccountCompanyForm';
import MyAccountCompanySearchForm from 'Component/MyAccountCompanySearchForm';
import MyAccountConfirmIdForm from 'Component/MyAccountConfirmIdForm';
import MyAccountSubscriptionFields from 'Component/MyAccountSubscriptionFields';
import StoreSwitcher from 'Component/StoreSwitcher';
import TextPlaceholder from 'Component/TextPlaceholder';
import { CHECKOUT_CUSTOMER_URL } from 'Route/Checkout/Checkout.config';
import { GROUP_ID_EMPLOYEE, GROUP_ID_STUDENT } from 'Route/MyAccount/MyAccount.config';
import {
    MyAccountCreateAccount as SourceMyAccountCreateAccount
} from 'SourceComponent/MyAccountCreateAccount/MyAccountCreateAccount.component';
import {
    AgreementType, CompanyDetailsType, CustomerFormValuesType, CustomerType
} from 'Type/Account.type';
import { RefType } from 'Type/Common.type';
import { isSignedIn } from 'Util/Auth';
import {
    FIRSTNAME_MAX_LENGTH,
    LASTNAME_MAX_LENGTH
} from 'Util/Customer';
import history from 'Util/History';
import { validateEmail } from 'Util/Validator';
import { VALIDATION_INPUT_TYPE } from 'Util/Validator/Config';

import {
    COMPANY_SEARCH_STEP,
    COMPANY_STEP,
    CUSTOMER_STEP,
    CUSTOMER_TYPES,
    OPTIN_STEP,
    RECAP_STEP,
    UPLOAD_STEP
} from './MyAccountCreateAccount.config';

import './MyAccountCreateAccount.override.style';

/** @namespace PeggysagePwa/Component/MyAccountCreateAccount/Component */
export class MyAccountCreateAccountComponent extends SourceMyAccountCreateAccount {
    static propTypes = {
        ...SourceMyAccountCreateAccount.propTypes,
        formRef: RefType.isRequired,
        isSiretEnabled: PropTypes.bool.isRequired,
        isCustomerTypeEmployeeEnabled: PropTypes.bool.isRequired,
        isCustomerTypeStudentEnabled: PropTypes.bool.isRequired,
        currentStoreCode: PropTypes.string.isRequired,
        customerType: PropTypes.string.isRequired,
        isCustomerTypeFieldHidden: PropTypes.bool.isRequired,
        createAccountStep: PropTypes.string.isRequired,
        companyDetails: CompanyDetailsType.isRequired,
        onCustomerTypeChanged: PropTypes.func.isRequired,
        onPhoneNumberChange: PropTypes.func.isRequired,
        phoneNumberError: PropTypes.string.isRequired,
        goToCompanyStep: PropTypes.func.isRequired,
        onSubmitCompanySearchForm: PropTypes.func.isRequired,
        onSubmitCompanyForm: PropTypes.func.isRequired,
        onSkipConfirmIdForm: PropTypes.func.isRequired,
        onSubmitConfirmIdForm: PropTypes.func.isRequired,
        hasMissingDocuments: PropTypes.bool.isRequired,
        onRecapButtonClick: PropTypes.func.isRequired,
        defaultValues: CustomerFormValuesType.isRequired,
        isCheckoutSuccess: PropTypes.bool.isRequired,
        registerAgreements: PropTypes.arrayOf(AgreementType).isRequired,
        isLoadingAgreements: PropTypes.bool.isRequired,
        onStoreSwitcherBeforeRedirect: PropTypes.func.isRequired,
        currentUploadStep: PropTypes.objectOf(PropTypes.string).isRequired,
        customer: CustomerType.isRequired
    };

    stepMapCustomerTypeInfo = {
        private: null,
        enterprise: __('Bienvenue à tous les professionnels de l\'esthétique et de la coiffure'),
        student: __('Bienvenue à tous les étudiants en esthétique et en coiffure'),
        employee: __('Bienvenue à tous les professionnels de l\'esthétique et de la coiffure')
    };

    stepMap = {
        [CUSTOMER_STEP]: {
            url: '',
            title: __('Create an account'),
            render: this.renderCustomerStep.bind(this)
        },
        [COMPANY_SEARCH_STEP]: {
            url: '/company-search',
            title: __('My company'),
            render: this.renderCompanySearchStep.bind(this)
        },
        [COMPANY_STEP]: {
            url: '/company',
            title: __('My company'),
            render: this.renderCompanyStep.bind(this)
        },
        [UPLOAD_STEP]: {
            url: '/identification',
            title: '',
            render: this.renderUploadStep.bind(this)
        },
        [OPTIN_STEP]: {
            url: '/optin',
            title: __('Finalisation'),
            render: this.renderOptinStep.bind(this)
        },
        [RECAP_STEP]: {
            url: '/success',
            title: '', // set in render function
            render: this.renderRecapStep.bind(this)
        }
    };

    renderPrivacyPolicy() {
        const { defaultValues } = this.props;

        if (isSignedIn()) {
            return null;
        }

        return (
            <fieldset block="MyAccountOverlay" elem="Legend" className="gdpr">
                <GdprConsent
                  location="registration"
                  mix={ { block: 'MyAccountOverlay', elem: 'Checkbox' } }
                  defaultValues={ defaultValues }
                />
            </fieldset>
        );
    }

    renderRegisterAgreements() {
        const { registerAgreements, isLoadingAgreements, customerType } = this.props;

        // For pro only
        if (customerType === CUSTOMER_TYPES.student) {
            return null;
        }

        if (isLoadingAgreements) {
            return (
                <fieldset block="MyAccountOverlay" elem="Legend" className="agreement">
                    <TextPlaceholder length="long" />
                </fieldset>
            );
        }

        if (!registerAgreements || !registerAgreements.length) {
            return null;
        }

        return registerAgreements.map((agreement, index) => (
            <fieldset
              block="MyAccountOverlay"
              elem="Legend"
              className="agreement"
              // eslint-disable-next-line react/no-array-index-key
              key={ index }
            >
                <FormAgreement
                  agreement={ agreement }
                  attr={ {
                      id: `register_agreement_${index}`,
                      name: `register_agreement_${index}`
                  } }
                  mix={ {
                      block: 'MyAccountOverlay',
                      elem: 'Checkbox'
                  } }
                />
            </fieldset>
        ));
    }

    /**
     * Override
     * @returns {JSX.Element}
     */
    renderSubscribeToNewsletter() {
        const {
            isCheckoutSuccess,
            defaultValues
        } = this.props;

        // NOTE: translations accept format token (%s) but args can only be numbers and strings, no JSX
        return (
            <fieldset
              block="MyAccountOverlay"
              elem="Legend"
              className="optins"
              mods={ { isHidden: isCheckoutSuccess } }
            >
                <MyAccountSubscriptionFields
                  defaultValues={ defaultValues }
                />
            </fieldset>
        );
    }

    /**
     * Override
     * @returns {JSX.Element}
     */
    renderCreateAccountPersonalInfoFields() {
        const { defaultValues: { firstname = '', lastname = '' }, isCheckoutSuccess } = this.props;

        return (
            <fieldset
              block="MyAccountOverlay"
              elem="Legend"
              className="name"
              mods={ { isHidden: isCheckoutSuccess } }
            >
                <Field
                  type={ FIELD_TYPE.text }
                  label={ __('First Name') }
                  attr={ {
                      id: 'firstname',
                      name: 'firstname',
                      defaultValue: firstname,
                      placeholder: __('First name'),
                      autoComplete: 'given-name'
                  } }
                  validateOn={ ['onChange'] }
                  validationRule={ {
                      inputType: VALIDATION_INPUT_TYPE.alphaSpaceDash,
                      isRequired: true,
                      match: (value) => value.length <= FIRSTNAME_MAX_LENGTH,
                      customErrorMessages: {
                          onMatchFail: __('La longueur du champ prénom ne peut pas dépasser %s caractères.', FIRSTNAME_MAX_LENGTH)
                      }
                  } }
                  addRequiredTag
                />
                <Field
                  type={ FIELD_TYPE.text }
                  label={ __('Last Name') }
                  attr={ {
                      id: 'lastname',
                      name: 'lastname',
                      defaultValue: lastname,
                      placeholder: __('Last name'),
                      autoComplete: 'family-name'
                  } }
                  validateOn={ ['onChange'] }
                  validationRule={ {
                      inputType: VALIDATION_INPUT_TYPE.alphaSpaceDash,
                      isRequired: true,
                      match: (value) => value.length <= LASTNAME_MAX_LENGTH,
                      customErrorMessages: {
                          onMatchFail: __('La longueur du champ nom ne peut pas dépasser %s caractères.', LASTNAME_MAX_LENGTH)
                      }
                  } }
                  addRequiredTag
                />
            </fieldset>
        );
    }

    renderCreateAccountEmailField() {
        const { defaultValues: { email = '' }, isCheckoutSuccess } = this.props;

        const readOnly = isCheckoutSuccess || isSignedIn();

        return (
            <Field
              type={ FIELD_TYPE.email }
              label={ __('Email') }
              attr={ {
                  id: 'email',
                  name: 'email',
                  defaultValue: email,
                  placeholder: __('Email'),
                  autoComplete: 'email',
                  readOnly: readOnly ? 'readonly' : false
              } }
              validateOn={ ['onChange'] }
              validationRule={ {
                  isRequired: true,
                  inputType: VALIDATION_INPUT_TYPE.email,
                  match: (value) => validateEmail(value)
              } }
              addRequiredTag
            />
        );
    }

    renderCreateAccountPasswordField() {
        const { defaultValues: { password = '' }, range, minimunPasswordCharacter } = this.props;

        if (isSignedIn()) {
            return null;
        }

        return (
            <div block="MyAccountOverlay" elem="PasswordBlock">
                <Field
                  type={ FIELD_TYPE.password }
                  label={ __('Password') }
                  subLabel={ __('Minimum %s characters!', range.min).toString() }
                  attr={ {
                      id: 'password',
                      name: 'password',
                      defaultValue: password,
                      placeholder: __('Password'),
                      autoComplete: 'new-password'
                  } }
                  validateOn={ ['onChange'] }
                  validationRule={ {
                      isRequired: true,
                      inputType: VALIDATION_INPUT_TYPE.password,
                      range,
                      match: (value) => {
                          // eslint-disable-next-line fp/no-let
                          let counter = 0;

                          if (value.match(/\d+/)) {
                              counter++;
                          }
                          if (value.match(/[a-z]+/)) {
                              counter++;
                          }
                          if (value.match(/[A-Z]+/)) {
                              counter++;
                          }
                          if (value.match(/[^a-zA-Z0-9]+/)) {
                              counter++;
                          }

                          return counter >= +minimunPasswordCharacter;
                      },
                      customErrorMessages: {
                          onMatchFail: __(
                              'Minimum of different classes of characters in password is %s. '
                              + 'Classes of characters: Lower Case, Upper Case, Digits, Special Characters.',
                              +minimunPasswordCharacter
                          )
                      }
                  } }
                  addRequiredTag
                />
            </div>
        );
    }

    /**
     * Override
     * @returns {JSX.Element}
     */
    renderCreateAccountSignUpInfoFields() {
        return (
            <fieldset block="MyAccountOverlay" elem="Legend" className="signup">
                { this.renderCreateAccountEmailField() }
                { this.renderCreateAccountPasswordField() }
            </fieldset>
        );
    }

    renderCreateCustomerTypeInfo() {
        const { customerType, isCheckoutSuccess, isCustomerTypeFieldHidden } = this.props;
        const isBlockVisbleLabel = this.stepMapCustomerTypeInfo[customerType];

        return (
            <fieldset
              block="MyAccountOverlayInfo"
              elem="Legend"
              className="type"
              mods={ { isHidden: !isBlockVisbleLabel || isCustomerTypeFieldHidden || isCheckoutSuccess } }
            >
              <div>{ isBlockVisbleLabel }</div>
            </fieldset>
        );
    }

    renderCreateCustomerType() {
        const {
            isCustomerTypeEmployeeEnabled,
            isCustomerTypeStudentEnabled,
            customerType,
            isCustomerTypeFieldHidden,
            onCustomerTypeChanged,
            isCheckoutSuccess,
            customer: { group_id: customerGroupId = 0 } = {}
        } = this.props;

        const customerTypes = [];

        if (!customerGroupId) {
            customerTypes.push({
                label: __('Private individual'),
                value: CUSTOMER_TYPES.private
            });
        }

        if (!isCheckoutSuccess) {
            customerTypes.push({
                label: __('Professional'),
                value: CUSTOMER_TYPES.pro
            });

            if (isCustomerTypeEmployeeEnabled && customerGroupId !== GROUP_ID_EMPLOYEE) {
                customerTypes.push({
                    label: __('Salarié'),
                    value: CUSTOMER_TYPES.employee
                });
            }

            if (isCustomerTypeStudentEnabled && customerGroupId !== GROUP_ID_STUDENT) {
                customerTypes.push({
                    label: __('Student'),
                    value: CUSTOMER_TYPES.student
                });
            }
        }

        return (
            <fieldset
              block="MyAccountOverlay"
              elem="Legend"
              className="type"
              mods={ { isHidden: isCustomerTypeFieldHidden || isCheckoutSuccess } }
            >
                <p>{ __('I am') }</p>
                <div className="Field-Wrapper_radios">
                    { customerTypes.map((type) => (
                        <Field
                          key={ type.value }
                          type={ FIELD_TYPE.radio }
                          label={ type.label }
                          attr={ {
                              id: `customer-type-${ type.value }`,
                              value: type.value,
                              name: 'customer_type',
                              checked: type.value === customerType
                          } }
                          events={ {
                              onChange: onCustomerTypeChanged
                          } }
                          mix={ { block: 'MyAccountOverlay', elem: 'Radio' } }
                        />
                    )) }
                </div>
            </fieldset>
        );
    }

    renderCreateAccountCountryFields() {
        const { isCheckoutSuccess, onStoreSwitcherBeforeRedirect } = this.props;

        return (
            <fieldset
              block="MyAccountOverlay"
              elem="Legend"
              className="country"
              mods={ { isHidden: isCheckoutSuccess } }
            >
                <StoreSwitcher onBeforeRedirect={ onStoreSwitcherBeforeRedirect } />
            </fieldset>
        );
    }

    renderCreateAccountPhoneFields() {
        const {
            onPhoneNumberChange, phoneNumberError, customerType, defaultValues: { telephone = '' }
        } = this.props;

        if (customerType === CUSTOMER_TYPES.student || customerType === CUSTOMER_TYPES.employee) {
            return null;
        }

        return (
            <fieldset block="MyAccountOverlay" elem="Legend">
                { /* <Field
                  type={ FIELD_TYPE.text }
                  label={ __('Phone number') }
                  attr={ {
                      id: 'telephone',
                      name: 'telephone',
                      defaultValue: telephone,
                      placeholder: __('Phone number')
                  } }
                  validateOn={ ['onChange'] }
                  validationRule={ {
                      isRequired: true,
                      inputType: VALIDATION_INPUT_TYPE.phone
                  } }
                  addRequiredTag
                /> */ }
                <FieldPhoneNumber
                  label={ __('Phone number') }
                  inputProps={ {
                      id: 'telephone',
                      name: 'telephone',
                      placeholder: __('Phone number'),
                      required: true,
                      defaultValue: telephone
                  } }
                  errorMessage={ phoneNumberError }
                  onChange={ onPhoneNumberChange }
                />
            </fieldset>
        );
    }

    renderNextButton() {
        return (
            <div block="MyAccountOverlay" elem="Buttons">
                <button
                  block="Button"
                  type="submit"
                  mix={ { block: 'MyAccountOverlay', elem: 'NextButton' } }
                >
                    { __('Continue') }
                </button>
            </div>
        );
    }

    renderCompanySearchStep() {
        const { goToCompanyStep, onSubmitCompanySearchForm } = this.props;

        return (
            <MyAccountCompanySearchForm
              onSkip={ goToCompanyStep }
              onSubmit={ onSubmitCompanySearchForm }
            />
        );
    }

    renderCompanyStep() {
        const {
            isSiretEnabled, companyDetails, onSubmitCompanyForm, defaultValues
        } = this.props;

        return (
            <MyAccountCompanyForm
              isSiretEnabled={ isSiretEnabled }
              companyDetails={ companyDetails }
              onSubmit={ onSubmitCompanyForm }
              defaultValues={ defaultValues }
            />
        );
    }

    renderUploadStep() {
        const {
            customerType,
            onSkipConfirmIdForm,
            onSubmitConfirmIdForm,
            defaultValues,
            currentUploadStep: {
                code,
                strings
            }
        } = this.props;

        return (
            <MyAccountConfirmIdForm
              customerType={ customerType }
              fileType={ code }
              formStrings={ strings }
              onSkip={ onSkipConfirmIdForm }
              onSubmit={ onSubmitConfirmIdForm }
              defaultValues={ defaultValues }
            />
        );
    }

    renderOptinStep() {
        return (
            <>
                { this.renderSubscribeToNewsletter() }
                { this.renderRegisterAgreements() }
                { this.renderPrivacyPolicy() }
                { this.renderSubmitButton() }
            </>
        );
    }

    renderRecapStepContent() {
        const { customerType, hasMissingDocuments } = this.props;

        if (customerType === CUSTOMER_TYPES.pro) {
            return (
                <>
                    { !hasMissingDocuments && (
                        <p>
                            { __('Les demandes de comptes pro sont validées en 2 jours ouvrés maximum.') }
                        </p>
                    ) }
                    { hasMissingDocuments && (
                        <>
                            <p>
                                { __('Votre ouverture de compte pro sera examinée quand vous aurez transmis '
                                    + 'les justificatifs manquants.') }
                            </p>
                            <p>
                                <strong>
                                    { __('Vous retrouverez dans l’email que nous venons de vous envoyer la marche '
                                        + 'à suivre pour nous transmettre les justificatifs manquants.') }
                                </strong>
                            </p>
                        </>
                    ) }
                    <p>
                        { __('En attendant la validation de votre compte vous pouvez passer une commande '
                            + 'mais vous ne bénéficierez pas des tarifs professionnels.') }
                    </p>
                </>
            );
        }

        if (customerType === CUSTOMER_TYPES.student) {
            return (
                <>
                    { !hasMissingDocuments && (
                        <p>
                            { __('Les demandes de comptes étudiants sont validées en 2 jours ouvrés maximum.') }
                        </p>
                    ) }
                    { hasMissingDocuments && (
                        <>
                            <p>
                                { __('Votre ouverture de compte étudiant sera examinée quand vous aurez transmis '
                                    + 'les justificatifs manquants.') }
                            </p>
                            <p>
                                <strong>
                                    { __('Vous retrouverez dans l’email que nous venons de vous envoyer la marche '
                                        + 'à suivre pour nous transmettre les justificatifs manquants.') }
                                </strong>
                            </p>
                        </>
                    ) }
                    <p>
                        { __('En attendant la validation de votre compte vous pouvez passer une commande '
                            + 'mais vous ne bénéficierez pas des tarifs étudiants.') }
                    </p>
                </>
            );
        }

        return null;
    }

    renderRecapStepButton() {
        const { onRecapButtonClick } = this.props;
        const { location: { pathname } } = history;

        const isInCheckout = pathname.includes(CHECKOUT_CUSTOMER_URL);

        const label = isInCheckout ? __('Continue my order') : __('Back to homepage');

        return (
            <button
              type="button"
              block="MyAccountCreateAccount"
              elem="RecapStepButton"
              mix={ { block: 'Button' } }
              onClick={ onRecapButtonClick }
            >
                { label }
            </button>
        );
    }

    renderRecapStep() {
        const { customerType } = this.props;

        // eslint-disable-next-line no-nested-ternary
        const title = customerType === CUSTOMER_TYPES.pro
            ? __('Your request to create an pro account has been sent')
            : customerType === CUSTOMER_TYPES.employee
                ? __('Your request to create an employee account has been sent')
                : __('Your request to create an student account has been sent');

        return (
            <div block="MyAccountCreateAccount" elem="RecapStep">
                <h3>{ title }</h3>
                { this.renderRecapStepContent() }
                { this.renderRecapStepButton() }
            </div>
        );
    }

    renderCustomerStep() {
        const { customerType } = this.props;

        if ([CUSTOMER_TYPES.pro, CUSTOMER_TYPES.student, CUSTOMER_TYPES.employee].includes(customerType)) {
            return (
                <>
                    { this.renderCreateCustomerType() }
                    { this.renderCreateCustomerTypeInfo() }
                    { this.renderCreateAccountSignUpInfoFields() }
                    { this.renderCreateAccountPersonalInfoFields() }
                    { this.renderCreateAccountPhoneFields() }
                    { this.renderNextButton() }
                </>
            );
        }

        return (
            <>
                { this.renderCreateCustomerType() }
                { this.renderCreateAccountSignUpInfoFields() }
                { this.renderCreateAccountPersonalInfoFields() }
                { this.renderCreateAccountCountryFields() }
                { this.renderSubscribeToNewsletter() }
                { this.renderPrivacyPolicy() }
                { this.renderSubmitButton() }
            </>
        );
    }

    renderStep() {
        const { createAccountStep } = this.props;
        const { render } = this.stepMap[createAccountStep];

        if (render) {
            return render();
        }

        return null;
    }

    renderDisclaimer() {
        return (
            <div block="MyAccountCreateAccount" elem="Disclaimer">
                <CmsBlock identifier="data_collected_disclaimer" />
            </div>
        );
    }

    /**
     * Override: add title, ref, step
     * @returns {JSX.Element}
     */
    renderCreateAccountForm() {
        const {
            onError, onSuccess, formRef, createAccountStep, currentUploadStep: { title: uploadStepTitle = '' }
        } = this.props;
        const { title: stepTitle = '' } = this.stepMap[createAccountStep];

        const title = createAccountStep === UPLOAD_STEP ? uploadStepTitle : stepTitle;

        return (
            <div block="MyAccountCreateAccount">
                { title && (
                    <h3>{ title }</h3>
                ) }
                <Form
                  key="create-account"
                  onSubmit={ onSuccess }
                  onError={ onError }
                  ref={ formRef }
                >
                    { this.renderStep() }
                </Form>
                { this.renderDisclaimer() }
            </div>
        );
    }

    /**
     * Override
     * @returns {JSX.Element}
     */
    render() {
        return this.renderCreateAccountForm();
    }
}

export default MyAccountCreateAccountComponent;
